import {
  PRODUCT_GROUP_LEAF_FIELDS_FRAGMENT,
  PRODUCT_SWATCH_DOT_FIELDS_FRAGMENT,
  PRODUCT_GROUP_SWATCH_EXPANSION_FRAGMENT,
  PRODUCT_COLOR_PATTERN_FIELDS_FRAGMENT,
  PRODUCT_FABRIC_REFERENCE_FIELDS_FRAGMENT,
  PRODUCT_GROUP_MAIN_IMAGES_FIELDS_FRAGMENT,
} from "./fragments";

// The wedding color palette offered by the showroom (showroom_configuration's
// show_colors list). Liquid only iterates the first 50 references of a list
// field, and the palette is longer than that, so it's resolved here instead.
export const SHOWROOM_COLORS_QUERY = `
  query ShowroomColors($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on Metaobject {
        id
        handle
        fields {
          key
          value
          reference {
            ... on MediaImage {
              image {
                url(transform: { maxWidth: 128 })
                altText
              }
            }
          }
        }
      }
    }
  }
`;

// Everything a showroom style card shows, for the variants (or, when an
// item has no variant id, the products) in a showroom's lineup/style JSON.
// `images` is scanned for the clean "showroom__" lineup image.
const SHOWROOM_STYLE_PRODUCT_FIELDS = `
  id
  title
  handle
  onlineStoreUrl
  featuredImage {
    url
    altText
  }
  images(first: 30) {
    nodes {
      url
      altText
    }
  }
  productGroup: metafield(namespace: "custom", key: "product_group") {
    id
    reference {
      ... on Metaobject {
        id
      }
    }
  }
  colorPattern: metafield(namespace: "shopify", key: "color-pattern") {
    references(first: 1) {
      nodes {
        ... on Metaobject {
          swatchLabel: field(key: "label") {
            value
          }
          swatchImage: field(key: "image") {
            reference {
              ... on MediaImage {
                image {
                  url(transform: { maxWidth: 64 })
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const SHOWROOM_STYLES_QUERY = `
  query ShowroomStyles($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on ProductVariant {
        id
        title
        image {
          url
          altText
        }
        price {
          amount
          currencyCode
        }
        product {
          ${SHOWROOM_STYLE_PRODUCT_FIELDS}
        }
      }
      ... on Product {
        ${SHOWROOM_STYLE_PRODUCT_FIELDS}
        variants(first: 1) {
          nodes {
            id
            title
            image {
              url
              altText
            }
            price {
              amount
              currencyCode
            }
          }
        }
      }
    }
  }
`;

export const PRODUCT_CARD_GROUPING_NODES_QUERY = `
  ${PRODUCT_GROUP_LEAF_FIELDS_FRAGMENT}

  query ProductCardGroupingNodes($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on Metaobject {
        id
        type
        handle

        ...ProductGroupLeafFields

        options: field(key: "product_group_options") {
          references(first: 250) {
            nodes {
              ... on Metaobject {
                id
                type
                handle

                ...ProductGroupLeafFields

                options: field(key: "product_group_options") {
                  references(first: 250) {
                    nodes {
                      ... on Metaobject {
                        id
                        type
                        handle

                        ...ProductGroupLeafFields
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const PRODUCT_SWATCH_COLOR_PATTERNS_QUERY = `
  ${PRODUCT_COLOR_PATTERN_FIELDS_FRAGMENT}

  query ProductSwatchColorPatterns($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on Product {
        id
        title
        handle
        productType
        availableForSale
        tags

        featuredImage {
          url
          altText
        }

        images(first: 2) {
          nodes {
            url
            altText
          }
        }

        priceRange {
          minVariantPrice {
            amount
            currencyCode
          }
        }

        variants(first: 1) {
          nodes {
            id
          }
        }

        badges: metafield(namespace: "custom", key: "badge") {
          value
        }

        deliveryTime: metafield(namespace: "custom", key: "delivery_time") {
          value
        }

        ...ProductColorPatternFields
      }
    }
  }
`;

export const PRODUCT_GROUP_SWATCH_EXPANSION_QUERY = `
  ${PRODUCT_GROUP_SWATCH_EXPANSION_FRAGMENT}
  ${PRODUCT_COLOR_PATTERN_FIELDS_FRAGMENT}
  ${PRODUCT_FABRIC_REFERENCE_FIELDS_FRAGMENT}

  query ProductGroupSwatchExpansion($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on Metaobject {
        id
        ...ProductGroupSwatchExpansionFields
      }
    }
  }
`;

export const COLLECTION_FILTERS_QUERY = `
  ${PRODUCT_COLOR_PATTERN_FIELDS_FRAGMENT}
  ${PRODUCT_GROUP_MAIN_IMAGES_FIELDS_FRAGMENT}

  query CollectionFilters(
    $handle: String!
    $filters: [ProductFilter!]
    $first: Int!
    $after: String
  ) {
    collection(handle: $handle) {
      id
      handle
      title

      excludeFilterGroups: metafield(
        namespace: "custom"
        key: "exclude_filter_groups"
      ) {
        value
      }

      products(
        first: $first
        after: $after
        filters: $filters
      ) {
        filters {
          id
          label
          type
          presentation

          values {
            id
            label
            count
            input

            image {
              image {
                url
                altText
              }

              previewImage {
                url
                altText
              }
            }

            swatch {
              color

              image {
                image {
                  url
                  altText
                }

                previewImage {
                  url
                  altText
                }
              }
            }
          }
        }

        nodes {
          id
          title
          handle
          productType
          availableForSale
          tags

          featuredImage {
            url
            altText
          }

          images(first: 2) {
            nodes {
              url
              altText
            }
          }

          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }

          variants(first: 1) {
            nodes {
              id
            }
          }

          deliveryTime: metafield(namespace: "custom", key: "delivery_time") {
            value
          }

          productGroup: metafield(
            namespace: "custom"
            key: "product_group"
          ) {
            reference {
              ... on Metaobject {
                id
                handle

                ...ProductGroupMainImagesFields
              }
            }
          }

          ...ProductColorPatternFields
        }

        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

export const SEARCH_FILTERS_QUERY = `
  ${PRODUCT_COLOR_PATTERN_FIELDS_FRAGMENT}
  ${PRODUCT_GROUP_MAIN_IMAGES_FIELDS_FRAGMENT}

  query SearchFilters(
    $query: String!
    $filters: [ProductFilter!]
    $first: Int!
    $after: String
  ) {
    search(
      query: $query
      types: [PRODUCT]
      productFilters: $filters
      first: $first
      after: $after
    ) {
      productFilters {
        id
        label
        type
        presentation

        values {
          id
          label
          count
          input

          image {
            image {
              url
              altText
            }

            previewImage {
              url
              altText
            }
          }

          swatch {
            color

            image {
              image {
                url
                altText
              }

              previewImage {
                url
                altText
              }
            }
          }
        }
      }

      edges {
        node {
          ... on Product {
            id
            title
            handle
            productType
            availableForSale
            tags

            featuredImage {
              url
              altText
            }

            images(first: 2) {
              nodes {
                url
                altText
              }
            }

            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }

            variants(first: 1) {
              nodes {
                id
              }
            }

            deliveryTime: metafield(namespace: "custom", key: "delivery_time") {
              value
            }

            productGroup: metafield(
              namespace: "custom"
              key: "product_group"
            ) {
              reference {
                ... on Metaobject {
                  id
                  handle

                  ...ProductGroupMainImagesFields
                }
              }
            }

            ...ProductColorPatternFields
          }
        }
      }

      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

// Search: just the tags of a result set, to list a tag filter group's options
// from results filtered by every other group (see
// fetchSearchTagGroupsWithoutOwnSelection in main-collection/api-facets.js).
export const SEARCH_FACET_TAGS_QUERY = `
  query SearchFacetTags($query: String!, $filters: [ProductFilter!], $first: Int!, $after: String) {
    search(query: $query, types: [PRODUCT], productFilters: $filters, first: $first, after: $after) {
      edges {
        node {
          ... on Product {
            tags
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const COLLECTION_GROUPED_COUNT_QUERY = `
  query CollectionGroupedCount(
    $handle: String!
    $filters: [ProductFilter!]
    $first: Int!
    $after: String
  ) {
    collection(handle: $handle) {
      products(first: $first, after: $after, filters: $filters) {
        nodes {
          id
          productType
          tags
          productGroup: metafield(namespace: "custom", key: "product_group") {
            reference {
              ... on Metaobject {
                id
              }
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

export const SEARCH_GROUPED_COUNT_QUERY = `
  query SearchGroupedCount(
    $query: String!
    $filters: [ProductFilter!]
    $first: Int!
    $after: String
  ) {
    search(query: $query, types: [PRODUCT], productFilters: $filters, first: $first, after: $after) {
      edges {
        node {
          ... on Product {
            id
            productType
            tags
            productGroup: metafield(namespace: "custom", key: "product_group") {
              reference {
                ... on Metaobject {
                  id
                }
              }
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

// Every product in a collection with its grouping inputs and swatch dot — the
// PLP's style group index (createStyleGroupIndex in
// main-collection/product-grouping.js), which also yields the unfiltered count.
export const COLLECTION_STYLE_GROUP_INDEX_QUERY = `
  ${PRODUCT_SWATCH_DOT_FIELDS_FRAGMENT}

  query CollectionStyleGroupIndex($handle: String!, $first: Int!, $after: String) {
    collection(handle: $handle) {
      products(first: $first, after: $after) {
        nodes {
          id
          productType
          tags
          productGroup: metafield(namespace: "custom", key: "product_group") {
            reference {
              ... on Metaobject {
                id
              }
            }
          }

          ...ProductSwatchDotFields
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

// Every product carrying one of a set of style_group: tags, store-wide (see
// createStyleGroupIndex in main-collection/product-grouping.js).
export const STYLE_GROUP_MEMBERS_QUERY = `
  ${PRODUCT_SWATCH_DOT_FIELDS_FRAGMENT}

  query StyleGroupMembers($query: String!, $first: Int!, $after: String) {
    products(first: $first, after: $after, query: $query) {
      nodes {
        id
        productType
        tags
        productGroup: metafield(namespace: "custom", key: "product_group") {
          reference {
            ... on Metaobject {
              id
            }
          }
        }

        ...ProductSwatchDotFields
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const SWATCH_PRODUCTS_FOR_FABRIC_MATCH_QUERY = `
  query SwatchProductsForFabricMatch($query: String!, $first: Int!) {
    search(query: $query, types: [PRODUCT], first: $first) {
      edges {
        node {
          ... on Product {
            id
            title
            handle
            availableForSale
            featuredImage {
              url
              altText
            }
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
            variants(first: 1) {
              nodes {
                id
              }
            }
            fabric: metafield(namespace: "custom", key: "fabric_new") {
              reference {
                ... on Metaobject {
                  id
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const PRODUCT_VARIANTS_PAGE_QUERY = `
  query ProductVariantsPage($id: ID!, $first: Int!, $after: String) {
    product(id: $id) {
      options(first: 3) {
        name
      }
      variants(first: $first, after: $after) {
        pageInfo {
          hasNextPage
          endCursor
        }
        nodes {
          id
          availableForSale
          price {
            amount
          }
          compareAtPrice {
            amount
          }
          selectedOptions {
            name
            value
          }
          variantNote: metafield(namespace: "custom", key: "variant_note") {
            type
            value
          }
        }
      }
    }
  }
`;
